import type { SignalMatch } from "@repo/app-types/entities/signalMatch";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { IconExternalLink } from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import { appPaths } from "../../../app/appPaths.js";
import {
  MatchAnalysisSlideoverScaffold,
  type MatchAnalysisMetadataEntry,
} from "../../transcriptViewer/matchAnalysisSlideover.js";
import {
  formatMetadataFieldKey,
  formatMetadataValue,
} from "../signalStatsFormatters.js";
import type { MetadataFieldLabel } from "./signalRecentMatches.js";

interface SignalMatchSlideoverDialogProps {
  match: SignalMatch;
  fields: readonly MetadataFieldLabel[];
  recordings: readonly AccountGradingRecording[];
  signalName: string;
}

function buildGradingUrl(match: SignalMatch): string {
  return appPaths.playbookCall.build({
    playbookId: match.playbookId,
    gradingId: match.gradingId,
  });
}

export const SignalMatchSlideoverDialog: FC<
  SignalMatchSlideoverDialogProps
> = ({ match, fields, recordings, signalName }) => {
  const metadataEntries = useMemo((): readonly MatchAnalysisMetadataEntry[] => {
    return Object.entries(match.metadata)
      .map(([fieldId, value]): MatchAnalysisMetadataEntry | null => {
        const field = fields.find((f) => f.fieldId === fieldId);
        if (!field) return null;
        return {
          label: formatMetadataFieldKey(field.fieldKey),
          value: formatMetadataValue(value),
        };
      })
      .filter((e): e is MatchAnalysisMetadataEntry => e !== null);
  }, [match.metadata, fields]);

  const gradingUrl = buildGradingUrl(match);

  return (
    <MatchAnalysisSlideoverScaffold
      title={signalName}
      subtitle={{
        contactName: match.contactName,
        contactLinkUrl: gradingUrl,
        recordedAt: match.lastRecordedAt,
      }}
      rightAction={
        <a
          href={gradingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
        >
          View grading
          <IconExternalLink className="size-3.5" />
        </a>
      }
      teammateName={match.teammateName}
      summaryText={match.explanation ?? undefined}
      metadataEntries={metadataEntries}
      analysis={match.analysis}
      gradingId={match.gradingId}
      recordings={recordings}
    />
  );
};
